/**
 * Regional configuration: locales we ship and markets we operate in.
 *
 * Wire preference codes live in `types.ts`. This module is the closed set of places we sell into.
 * Locales and markets stay separate — a country can ship more than one locale (CA) without
 * repeating currency.
 *
 * Each market’s `country` is ISO 3166-1 alpha-2 and is a **wire value** (Zuora payment-method
 * region, org country of operation, etc.). It is a field on purpose — not an object key — so renaming it for
 * “readability” (e.g. `USA`) is an obvious data change, not a harmless key tweak.
 *
 * TODO: Drop per-market `currency` once `/me` `effectiveUserPreferences` exposes the organization's
 * country of operation and currency. Callers should read those instead of mapping country → ISO
 * here (billing currency follows org country of operation, not user locale).
 */

const REGIONAL_CONFIG = {
  locales: ['en-US', 'en-CA', 'fr-CA', 'es-MX'],
  markets: [
    { country: 'US', currency: 'USD', regionFieldLabel: 'state' },
    { country: 'CA', currency: 'CAD', regionFieldLabel: 'province' },
    { country: 'MX', currency: 'MXN', regionFieldLabel: 'state' },
  ],
} as const;

type Market = (typeof REGIONAL_CONFIG.markets)[number];

/** BCP-47 locales the product ships (i18n directories and user-selectable preferences). */
export const SUPPORTED_LOCALES = REGIONAL_CONFIG.locales;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export type OperatingCountry = Market['country'];
export type CurrencyCode = Market['currency'];
export type RegionFieldLabel = Market['regionFieldLabel'];

/** ISO 3166-1 alpha-2 countries of operation (Zuora region, org country of operation, etc.). */
export const OPERATING_COUNTRIES = REGIONAL_CONFIG.markets.map(
  (market) => market.country
);

/** Billing currency (ISO 4217) keyed by market `country`. */
export const CURRENCY_BY_COUNTRY = Object.fromEntries(
  REGIONAL_CONFIG.markets.map((market) => [market.country, market.currency])
) as { readonly [C in OperatingCountry]: CurrencyCode };

/** Subdivision field label (`state` or `province`) keyed by market `country`. */
export const REGION_FIELD_LABEL_BY_COUNTRY = Object.fromEntries(
  REGIONAL_CONFIG.markets.map((market) => [
    market.country,
    market.regionFieldLabel,
  ])
) as { readonly [C in OperatingCountry]: RegionFieldLabel };

/**
 * When context is missing: which locale + country to use (currency/language follow from those).
 *
 * `@brightdrop/auth-client` holds a copy of `.locale` for the `cs-locale` request header sent
 * before a reader is known. It cannot import this package — the core is federation-shared by one
 * app and that one by four — so a test in `fleet-management`, which depends on both, holds the
 * two equal.
 */
export const REGIONAL_DEFAULTS = {
  locale: 'en-US',
  country: 'US',
  currency: CURRENCY_BY_COUNTRY.US,
  language: 'en',
} as const satisfies {
  readonly locale: SupportedLocale;
  readonly country: OperatingCountry;
  readonly currency: CurrencyCode;
  readonly language: string;
};

/**
 * ISO 4217 codes the platform bills in.
 *
 * The backend sends a *style* (`USD_STYLE`), not an ISO code; `CURRENCY_CODE_BY_STYLE` maps
 * style → ISO. That map must stay parallel with this list.
 */
export const CURRENCY_CODES = REGIONAL_CONFIG.markets.map(
  (market) => market.currency
);
